import React, { useEffect, useState } from 'react';
import {
  Table, Button, Space, Typography, Popconfirm, Modal, message, Tag, Tooltip, Spin,
} from 'antd';
import {
  ArrowLeftOutlined, HistoryOutlined, DeleteOutlined, ReloadOutlined,
  MailOutlined, RedoOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';

import { oilOrdersApi, OilOrderResponse, OilOrderItem, OilSupplier } from '../api/oilOrders';
import useOilCartStore from '../store/oilCartStore';
import useAuthStore from '../store/authStore';
import useTaskStore from '../store/taskStore';
import { useNevaCart } from '../hooks/useNevaCart';

const { Title } = Typography;

export default function OilOrdersHistoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState<OilOrderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const { loadCart, items: cartItems } = useOilCartStore();
  const user = useAuthStore(s => s.user);
  const isAdmin = user?.role === 'admin';
  const [expandedKeys, setExpandedKeys] = useState<number[]>([]);
  const [suppliers, setSuppliers] = useState<OilSupplier[]>([]);
  const { markPendingIfHasNeva, checkAndOpen, markPending } = useNevaCart(suppliers);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      setOrders(await oilOrdersApi.getOrders());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    oilOrdersApi.getSuppliers().then(setSuppliers);
    fetchOrders();
    // Пока на странице истории — подавляем popup для задач отправки заказов,
    // потому что статус виден прямо в таблице + useNevaCart сам откроет вкладку
    useTaskStore.getState().suppressType('oil_order_send');
    return () => { useTaskStore.getState().unsuppressType('oil_order_send'); };
  }, []);

  // При переходе из корзины — пометить заказ как ожидающий открытия НЕВА
  useEffect(() => {
    const state = location.state as { nevaOrderId?: number; nevaItems?: { supplier_id: number }[] } | null;
    if (state?.nevaOrderId && state?.nevaItems && suppliers.length > 0) {
      markPendingIfHasNeva(state.nevaOrderId, state.nevaItems);
      // Очистить state чтобы повторно не срабатывало
      navigate(location.pathname, { replace: true });
    }
  }, [suppliers, location.state, markPendingIfHasNeva, navigate, location.pathname]);

  // Polling: пока есть emails со статусом "sending" — обновляем каждые 5 сек
  useEffect(() => {
    const hasSending = orders.some(o => o.emails?.some(e => e.status === 'sending'));
    if (!hasSending) return;
    const timer = setInterval(async () => {
      try {
        const fresh = await oilOrdersApi.getOrders();
        setOrders(fresh);
        checkAndOpen(fresh);
        if (!fresh.some(o => o.emails?.some(e => e.status === 'sending'))) {
          clearInterval(timer);
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(timer);
  }, [orders, checkAndOpen]);

  const handleRepeat = (orderId: number) => {
    Modal.confirm({
      title: 'Повторить заказ?',
      content: 'Вся текущая корзина при этом будет очищена.',
      okText: 'Повторить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const res = await oilOrdersApi.repeatOrder(orderId);
          message.success(res.message);
          await loadCart();
          navigate('/oil-orders/cart');
        } catch {
          message.error('Ошибка при повторении заказа');
        }
      },
    });
  };

  const handleDelete = async (orderId: number) => {
    try {
      await oilOrdersApi.deleteOrder(orderId);
      message.success('Заказ удалён');
      setOrders(prev => prev.filter(o => o.id !== orderId));
    } catch {
      message.error('Ошибка при удалении');
    }
  };

  const handleResend = async (orderId: number, supplierId: number) => {
    try {
      await oilOrdersApi.resendOrderEmail(orderId, supplierId);
      markPending(orderId, supplierId);
      message.info('Повторная отправка...');
      setOrders(prev => prev.map(o => {
        if (o.id !== orderId) return o;
        return {
          ...o,
          emails: o.emails.map(e =>
            e.supplier_id === supplierId ? { ...e, status: 'sending' as const, error_message: null } : e
          ),
        };
      }));
      // polling в useEffect подхватит "sending" и будет опрашивать
    } catch {
      message.error('Ошибка повторной отправки');
    }
  };

  const columns = [
    {
      title: '№', dataIndex: 'id', width: 70,
    },
    {
      title: 'Дата', dataIndex: 'created_at', width: 160,
      render: (v: string) => new Date(v).toLocaleString('ru'),
    },
    {
      title: 'Кто', dataIndex: 'created_by_name', width: 150,
    },
    {
      title: 'Позиций', dataIndex: 'total_qty', width: 100,
      render: (v: number) => `${v} шт`,
    },
    {
      title: 'Сумма', dataIndex: 'total_sum', width: 140,
      render: (v: number) => `${v.toLocaleString('ru', { minimumFractionDigits: 2 })} ₽`,
    },
    {
      title: 'Отправка', key: 'email_status', width: 200,
      render: (_: any, order: OilOrderResponse) => {
        const emails = order.emails || [];
        if (!emails.length) return <span style={{ color: '#999' }}>—</span>;

        const sending = emails.filter(e => e.status === 'sending');
        const sent = emails.filter(e => e.status === 'sent');
        const errors = emails.filter(e => e.status === 'error');

        return (
          <div onClick={e => e.stopPropagation()}>
            {sending.length > 0 && (
              <span style={{ color: '#1677ff', marginRight: 8 }}>
                <Spin size="small" style={{ marginRight: 4 }} />
                Отправляется...
              </span>
            )}
            {sent.map(e => (
              <Tag
                key={e.supplier_id}
                color="green"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  Modal.confirm({
                    title: 'Повторить отправку?',
                    content: `Отправить заказ повторно поставщику «${e.supplier_name}»?`,
                    okText: 'Отправить',
                    cancelText: 'Отмена',
                    onOk: () => handleResend(order.id, e.supplier_id),
                  });
                }}
              >
                <MailOutlined style={{ marginRight: 4 }} />
                {e.supplier_name} отправлено
              </Tag>
            ))}
            {errors.map(e => (
              <span key={e.supplier_id}>
                <Tooltip title={e.error_message || 'Неизвестная ошибка'}>
                  <Tag color="red" style={{ cursor: 'help' }}>
                    {e.supplier_name} — ошибка
                  </Tag>
                </Tooltip>
                <Tooltip title="Повторить отправку">
                  <RedoOutlined
                    style={{ color: '#1677ff', cursor: 'pointer', marginLeft: 4 }}
                    onClick={() => handleResend(order.id, e.supplier_id)}
                  />
                </Tooltip>
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: '', key: 'actions', width: 200,
      render: (_: any, order: OilOrderResponse) => (
        <div onClick={e => e.stopPropagation()}>
          <Space>
            <Button size="small" icon={<ReloadOutlined />}
              onClick={() => handleRepeat(order.id)}>
              Повторить
            </Button>
            {isAdmin && (
              <Popconfirm title="Удалить заказ?" onConfirm={() => handleDelete(order.id)}
                okText="Удалить" cancelText="Отмена" okType="danger">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        </div>
      ),
    },
  ];

  function renderProductTypes(types: string[] | null | undefined) {
    if (!types || types.length === 0) return <span>—</span>;
    return <span style={{ whiteSpace: 'pre-line' }}>{types.join(',\n')}</span>;
  }

  const expandedRowRender = (order: OilOrderResponse) => {
    const itemColumns = [
      { title: 'Тип', dataIndex: 'product_types', width: 100, render: (v: string[] | null) => renderProductTypes(v) },
      { title: 'Бренд', dataIndex: 'brand', width: 100 },
      { title: 'Произв.', dataIndex: 'origin', width: 50, render: (v: string | null) => v || '' },
      { title: 'Модель', dataIndex: 'model_name' },
      { title: 'Артикул', dataIndex: 'article', width: 100, render: (v: string | null) => v || '' },
      { title: 'Тара', dataIndex: 'container_size', width: 80 },
      { title: 'Поставщик', dataIndex: 'supplier_name', width: 150 },
      {
        title: 'Кол-во', dataIndex: 'qty', width: 80,
        render: (v: number) => `${v} шт`,
      },
      {
        title: 'Цена', dataIndex: 'price', width: 100,
        render: (v: number) => `${v.toLocaleString('ru', { minimumFractionDigits: 2 })} ₽`,
      },
      {
        title: 'Сумма', key: 'sum', width: 120,
        render: (_: any, item: OilOrderItem) =>
          `${(item.qty * item.price).toLocaleString('ru', { minimumFractionDigits: 2 })} ₽`,
      },
    ];

    return (
      <Table
        columns={itemColumns}
        dataSource={order.items}
        rowKey="id"
        pagination={false}
        size="small"
      />
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(cartItems.length > 0 ? '/oil-orders/cart' : '/oil-orders')}>
          Назад
        </Button>
      </Space>

      <Title level={3}>
        <HistoryOutlined style={{ marginRight: 8 }} />
        История закупок
      </Title>

      <Table
        columns={columns}
        dataSource={orders}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20 }}
        expandable={{
          expandedRowRender,
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => setExpandedKeys(keys as number[]),
        }}
        onRow={(record) => ({
          onClick: () => setExpandedKeys(prev =>
            prev.includes(record.id) ? prev.filter(k => k !== record.id) : [...prev, record.id]
          ),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
